/**
 * Format a Date as value for <input type="datetime-local" /> (local timezone, no seconds).
 * @param {Date} d
 * @returns {string|null}
 */
export function formatAsDateTimeLocal(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

/**
 * Parse YYYY-MM-DDTHH:mm (datetime-local) or YYYY-MM-DD as local wall time.
 * @param {string|null|undefined} s
 * @returns {Date|null}
 */
export function parseDateTimeLocalToDate(s) {
  if (s == null || s === "") return null;
  const t = String(s).trim();
  if (!t) return null;
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (m) {
    return new Date(
      parseInt(m[1], 10),
      parseInt(m[2], 10) - 1,
      parseInt(m[3], 10),
      parseInt(m[4], 10),
      parseInt(m[5], 10),
      m[6] ? parseInt(m[6], 10) : 0,
      0
    );
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
    const [y, mo, d] = t.split("-").map((x) => parseInt(x, 10));
    return new Date(y, mo - 1, d, 0, 0, 0, 0);
  }
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d;
}

const MS_PER_DAY = 86400000;
const MS_PER_HOUR = 3600000;

/** Lowercase alphanumeric-only key for loose name / id matching. */
function normKey(name) {
  return String(name ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * Map API / cache rows to a single shape (snake_case + camelCase + odd casings).
 * @param {Record<string, unknown>} r
 * @returns {{ application_id: number, priority_id: number, application_name: string, priority_name: string, sla_duration_days: unknown, sla_duration_hours: unknown } | null}
 */
function canonicalSlaRow(r) {
  if (!r || typeof r !== "object") return null;
  const application_id = Number(
    r.application_id ?? r.applicationId ?? r.Application_Id ?? r.APPLICATION_ID
  );
  const priority_id = Number(
    r.priority_id ?? r.priorityId ?? r.Priority_Id ?? r.PRIORITY_ID
  );
  if (Number.isNaN(application_id) || Number.isNaN(priority_id)) return null;
  return {
    application_id,
    priority_id,
    application_name: String(r.application_name ?? r.applicationName ?? ""),
    priority_name: String(r.priority_name ?? r.priorityName ?? ""),
    sla_duration_days:
      r.sla_duration_days ?? r.slaDurationDays ?? r.sla_duration_day ?? r.Sla_Duration_Days,
    sla_duration_hours: r.sla_duration_hours ?? r.slaDurationHours ?? r.Sla_Duration_Hours,
  };
}

/**
 * @param {string|number} applicationValue
 * @param {Array<Record<string, unknown>>} applications
 * @returns {number}
 */
function resolveApplicationIdFromLookups(applicationValue, applications) {
  const s = String(applicationValue ?? "").trim();
  if (!s) return NaN;
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  if (!Array.isArray(applications) || applications.length === 0) return NaN;
  const byName = applications.find(
    (a) => normKey(a.aa_app_rl_application_name) === normKey(s)
  );
  return byName ? Number(byName.aa_app_rl_application_id) : NaN;
}

/**
 * @param {Array<Record<string, unknown>>} rows canonical SLA rows
 * @param {number} appId
 * @param {number} priId
 * @param {{ priorityName?: string, applicationName?: string }} [options]
 */
function findSlaRow(rows, appId, priId, options = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const appNameK = options.applicationName ? normKey(options.applicationName) : "";
  const priNameK = options.priorityName ? normKey(options.priorityName) : "";

  const byAppPri = list.find(
    (r) => r.application_id === appId && r.priority_id === priId
  );
  if (byAppPri) return byAppPri;

  const byAppPriName = list.find(
    (r) =>
      r.application_id === appId &&
      priNameK &&
      normKey(r.priority_name) === priNameK
  );
  if (byAppPriName) return byAppPriName;

  const byAppNamePri = list.find(
    (r) =>
      appNameK &&
      normKey(r.application_name) === appNameK &&
      r.priority_id === priId
  );
  if (byAppNamePri) return byAppNamePri;

  const byAppNamePriName = list.find(
    (r) =>
      appNameK &&
      priNameK &&
      normKey(r.application_name) === appNameK &&
      normKey(r.priority_name) === priNameK
  );
  return byAppNamePriName ?? null;
}

/**
 * @param {Record<string, unknown>} row
 * @param {{ priorityName?: string }} [options] selected priority label when row omits `priority_name`
 * @returns {number|null} duration in ms, or null if not computable
 */
function slaDurationMsFromRow(row, options = {}) {
  if (!row || typeof row !== "object") return null;

  const hoursRaw = row.sla_duration_hours;
  if (hoursRaw != null && hoursRaw !== "") {
    const h = parseFloat(String(hoursRaw).replace(/,/g, "."));
    if (!Number.isNaN(h) && h > 0) return Math.round(h * MS_PER_HOUR);
  }

  const daysRaw =
    row.sla_duration_days ?? row.slaDurationDays ?? row.sla_duration_day;
  if (daysRaw == null || daysRaw === "") return null;

  let days = parseFloat(String(daysRaw).replace(/,/g, "."));
  if (Number.isNaN(days) || days < 0) return null;

  const pname = normKey(row.priority_name);
  const optPri = normKey(options.priorityName);
  const critById = Number(options.priorityId) === 4;
  if (
    days === 0 &&
    (pname.includes("critical") || optPri.includes("critical") || critById)
  ) {
    days = 0.5;
  }

  return Math.round(days * MS_PER_DAY);
}

/**
 * Expected due datetime from SLA: start + duration.
 * - Supports fractional `sla_duration_days` (e.g. Critical **0.5** = **12 hours**).
 * - Optional `sla_duration_hours` on the row (hours take precedence when set).
 * - Matches SLA row by application + priority ids; falls back to **application_name** / **priority_name**
 *   when ids differ between `application_details` / `task_priority_details` and `task_sla_details`.
 *
 * @param {Array<Record<string, unknown>>} taskSlaDetails
 * @param {string|number} applicationId
 * @param {string|number} priorityId
 * @param {string|null|undefined} startDateTimeLocal YYYY-MM-DDTHH:mm (or YYYY-MM-DD)
 * @param {{ priorityName?: string, applicationName?: string, applications?: Array<Record<string, unknown>> }} [options]
 * @returns {string|null} YYYY-MM-DDTHH:mm for datetime-local, or null
 */
export function computeDueDateFromSla(
  taskSlaDetails,
  applicationId,
  priorityId,
  startDateTimeLocal,
  options = {}
) {
  if (!Array.isArray(taskSlaDetails) || taskSlaDetails.length === 0) return null;

  const rows = taskSlaDetails.map(canonicalSlaRow).filter(Boolean);
  if (!rows.length) return null;

  let appId = parseInt(String(applicationId), 10);
  if (Number.isNaN(appId) && Array.isArray(options.applications)) {
    appId = resolveApplicationIdFromLookups(applicationId, options.applications);
  }
  const priId = parseInt(String(priorityId), 10);
  if (Number.isNaN(appId) || Number.isNaN(priId)) return null;

  const row = findSlaRow(rows, appId, priId, options);
  if (!row) return null;

  const durationMs = slaDurationMsFromRow(row, {
    ...options,
    priorityId: priId,
  });
  if (durationMs == null || durationMs < 0) return null;

  const base = parseDateTimeLocalToDate(startDateTimeLocal);
  if (!base) return null;

  const due = new Date(base.getTime() + durationMs);
  return formatAsDateTimeLocal(due);
}

/**
 * Wrapper around {@link computeDueDateFromSla} that supplies lookup names and `applications`
 * so application id can be resolved and SLA rows match reliably.
 *
 * @param {Array<Record<string, unknown>>} taskSlaDetails
 * @param {string|number} applicationId
 * @param {string|number} priorityId
 * @param {string|null|undefined} startDateTimeLocal
 * @param {Array<Record<string, unknown>>} [applications]
 * @param {Array<Record<string, unknown>>} [priorities]
 * @returns {string|null}
 */
export function computeDueDateFromSlaWithLookups(
  taskSlaDetails,
  applicationId,
  priorityId,
  startDateTimeLocal,
  applications = [],
  priorities = []
) {
  const apps = Array.isArray(applications) ? applications : [];
  const pris = Array.isArray(priorities) ? priorities : [];
  const appRow = apps.find(
    (a) => String(a.aa_app_rl_application_id) === String(applicationId)
  );
  const priRow = pris.find(
    (p) => String(p.aa_app_rl_priority_id) === String(priorityId)
  );
  return computeDueDateFromSla(
    taskSlaDetails,
    applicationId,
    priorityId,
    startDateTimeLocal,
    {
      priorityName: priRow?.aa_app_rl_priority_name,
      applicationName: appRow?.aa_app_rl_application_name,
      applications: apps,
    }
  );
}

/**
 * Normalize server date/time strings for <input type="datetime-local" />.
 * @param {string|number|Date|null|undefined} dateStr
 * @returns {string} YYYY-MM-DDTHH:mm or ''
 */
export function toDateTimeLocalInputValue(dateStr) {
  if (dateStr == null || dateStr === "") return "";
  if (dateStr instanceof Date) {
    return formatAsDateTimeLocal(dateStr) || "";
  }

  const s = String(dateStr).trim();
  if (!s) return "";

  // Date-only: avoid Date('YYYY-MM-DD') UTC-shift; pin to start of that calendar day (local).
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return `${s}T00:00`;
  }

  // SQL / API naive local: "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD HH:mm"
  if (/^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}/.test(s)) {
    let t = s.replace(/^(\d{4}-\d{2}-\d{2})\s+/, "$1T");
    if (t.length > 16) t = t.slice(0, 16);
    const parsed = parseDateTimeLocalToDate(t);
    if (parsed && !Number.isNaN(parsed.getTime())) {
      return formatAsDateTimeLocal(parsed) || "";
    }
  }

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return formatAsDateTimeLocal(d) || "";
}
